import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'

// Initial state
const initialState = {
  matrixData: null,
  fullCategoryData: {}, // Store subcategory data for expand operations
  loading: false,
  error: null,
  expandedCategory: null, // Single category name (accordion behavior)
  transactions: null,
  transactionsLoading: false,
  transactionsError: null,
  customers: [], // Epic 5.3: Customer list for assignment
  searchResults: null, // Search results for transaction search
  searchLoading: false,
  searchError: null
}

// Thunk: Fetch matrix data from API
export const fetchMatrix = createAsyncThunk(
  'spendTracking/fetchMatrix',
  async ({ months = 12, startDate = null, year = null }, { rejectWithValue }) => {
    try {
      const params = { months }
      if (startDate) {
        params.startDate = startDate
      }
      if (year) {
        params.year = year
      }

      const response = await axios.get('/v1/spends/matrix', { params })
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch spend data'
      )
    }
  }
)

// Thunk: Search transactions (for filtering by transaction ID, comment, etc.)
export const searchSpendTransactions = createAsyncThunk(
  'spendTracking/searchTransactions',
  async ({ query, year }, { rejectWithValue }) => {
    try {
      const params = { query }
      if (year) {
        params.year = year
      }

      const response = await axios.get('/v1/spends/search', { params })
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to search transactions'
      )
    }
  }
)

// Thunk: Fetch transactions for a specific category and month
export const fetchTransactions = createAsyncThunk(
  'spendTracking/fetchTransactions',
  async ({ category, expenseCategory, month }, { rejectWithValue }) => {
    try {
      const params = { category, month }
      if (expenseCategory) {
        params.expenseCategory = expenseCategory
      }

      const response = await axios.get('/v1/spends/transactions', { params })
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch transactions'
      )
    }
  }
)

// Thunk: Update transaction annotations (Epic 5.3)
export const updateTransactionAnnotations = createAsyncThunk(
  'spendTracking/updateAnnotations',
  async ({ spendId, updates }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`/v1/spends/${spendId}/annotations`, updates)
      return response.data.spend
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to update annotations'
      )
    }
  }
)

// Thunk: Toggle transaction flag (Epic 5.3 Story 5.3.4)
export const toggleTransactionFlag = createAsyncThunk(
  'spendTracking/toggleTransactionFlag',
  async ({ spendId, currentStatus }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`/v1/spends/${spendId}/annotations`, {
        flagged: !currentStatus
      })
      return response.data.spend
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to toggle flag'
      )
    }
  }
)

// Thunk: Fetch customers for assignment (Epic 5.3 Story 5.3.5)
export const fetchCustomers = createAsyncThunk(
  'spendTracking/fetchCustomers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/v1/customers')
      return response.data.customers || response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to fetch customers'
      )
    }
  }
)

// Thunk: Assign customer to transaction (Epic 5.3 Story 5.3.5)
export const assignCustomerToTransaction = createAsyncThunk(
  'spendTracking/assignCustomerToTransaction',
  async ({ spendId, customerId }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`/v1/spends/${spendId}/annotations`, {
        customerId
      })
      return response.data.spend
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to assign customer'
      )
    }
  }
)

// Slice
const spendTrackingSlice = createSlice({
  name: 'spendTracking',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    toggleCategory: (state, action) => {
      const categoryName = action.payload
      // Accordion: if clicking same category, collapse it; otherwise expand new one
      if (state.expandedCategory === categoryName) {
        state.expandedCategory = null
      } else {
        state.expandedCategory = categoryName
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatrix.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMatrix.fulfilled, (state, action) => {
        state.loading = false
        state.matrixData = action.payload

        // Store subcategory data separately for expand/collapse operations
        if (action.payload && action.payload.categories) {
          state.fullCategoryData = action.payload.categories.reduce((acc, cat) => {
            acc[cat.parentCategory] = cat.subcategoryData || []
            return acc
          }, {})
        }
      })
      .addCase(fetchMatrix.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(fetchTransactions.pending, (state) => {
        state.transactionsLoading = true
        state.transactionsError = null
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.transactionsLoading = false
        state.transactions = action.payload
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.transactionsLoading = false
        state.transactionsError = action.payload
      })
      .addCase(updateTransactionAnnotations.fulfilled, (state, action) => {
        // Update the transaction in the transactions list
        if (state.transactions && state.transactions.data) {
          const index = state.transactions.data.findIndex(t => t.id === action.payload.id)
          if (index !== -1) {
            state.transactions.data[index] = {
              ...state.transactions.data[index],
              ...action.payload
            }
          }
        }
      })
      .addCase(toggleTransactionFlag.fulfilled, (state, action) => {
        // Update the transaction in the transactions list
        if (state.transactions && state.transactions.data) {
          const index = state.transactions.data.findIndex(t => t.id === action.payload.id)
          if (index !== -1) {
            state.transactions.data[index] = {
              ...state.transactions.data[index],
              ...action.payload
            }
          }
        }
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.customers = action.payload
      })
      .addCase(assignCustomerToTransaction.fulfilled, (state, action) => {
        // Update the transaction in the transactions list
        if (state.transactions && state.transactions.data) {
          const index = state.transactions.data.findIndex(t => t.id === action.payload.id)
          if (index !== -1) {
            state.transactions.data[index] = {
              ...state.transactions.data[index],
              ...action.payload
            }
          }
        }
      })
      .addCase(searchSpendTransactions.pending, (state) => {
        state.searchLoading = true
        state.searchError = null
      })
      .addCase(searchSpendTransactions.fulfilled, (state, action) => {
        state.searchLoading = false
        state.searchResults = action.payload
      })
      .addCase(searchSpendTransactions.rejected, (state, action) => {
        state.searchLoading = false
        state.searchError = action.payload
      })
  }
})

export const { clearError, toggleCategory } = spendTrackingSlice.actions
export default spendTrackingSlice.reducer
